import { useState } from 'react';
import { tallyVote, type AgendaItem, type Attendee, type Meeting, type UpdateAttendance, type RecordVotes } from '@syndiqia/governance/contracts';
import { errorMessage } from '../../api';
import { Alert } from '../../components/ui';
import { useGovernanceMutation } from '../../governance-queries';
import { useI18n } from '../../i18n';

const pct = (n: number, total: number, locale: 'fr' | 'en') =>
  `${new Intl.NumberFormat(locale === 'fr' ? 'fr-CA' : 'en-CA', { maximumFractionDigits: 2 }).format(total ? (n / total) * 100 : 0)} %`;

/** Attendance register with the live quorum (step 3, before the notes). */
export function AttendancePanel({ meeting, editable }: { meeting: Meeting; editable: boolean }) {
  const { t, locale } = useI18n();
  const g = t.governance;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(meeting.attendance.length === 0 || meeting.status !== 'closed');
  const prepare = useGovernanceMutation<void, Meeting>((api, base) => api(`${base}/governance/meetings/${meeting.id}/attendance/prepare`, { method: 'POST' }));
  const hint = meeting.kind === 'board' ? g.attendanceHintBoard : meeting.voteBasis === 'lots' ? g.attendanceHintLots : meeting.voteBasis === 'persons' ? g.attendanceHintPersons : g.attendanceHint;
  const anyVote = meeting.items.some((i) => Object.keys(i.votes).length > 0);

  if (meeting.attendance.length === 0) {
    return (
      <section className="panel">
        <h2>{g.attendance}</h2>
        <p className="muted small">{meeting.kind === 'board' ? g.attendanceHintBoard : g.attendanceHint}</p>
        {prepare.isError && <Alert>{errorMessage(prepare.error, t)}</Alert>}
        {editable && (
          <button type="button" className="button button-primary" disabled={prepare.isPending} onClick={() => prepare.mutate()}>
            {g.prepareAttendance}
          </button>
        )}
      </section>
    );
  }

  const q = meeting.quorum!;
  const shown = meeting.attendance.filter((a) => !query || `${a.name} ${a.units}`.toLowerCase().includes(query.toLowerCase()));
  return (
    <section className="panel">
      <header className="dash-card-header">
        <h2>{g.attendance}</h2>
        <button type="button" className="link-button small" onClick={() => setOpen(!open)}>
          {open ? '▴' : '▾'}
        </button>
      </header>
      <div className={`quorum ${q.reached ? 'quorum-ok' : 'quorum-ko'}`}>
        <div className="quorum-bar">
          <span style={{ width: `${Math.min(100, q.ratio * 100)}%` }} />
          <i style={{ left: `${q.threshold * 100}%` }} />
        </div>
        <strong>{q.reached ? g.quorumReached : g.quorumNotReached}</strong>
        <span className="muted small">{g.quorumLine(pct(q.presentVotes, q.totalVotes, locale), q.presentCount, meeting.totalVoters ?? 0)}</span>
      </div>
      {open && (
        <>
          <p className="muted small">{hint}</p>
          {meeting.attendance.length > 8 && <input className="search" type="search" placeholder={g.search} value={query} onChange={(e) => setQuery(e.target.value)} />}
          <ul className="attendance">
            {shown.map((a) => (
              <AttendanceRow key={a.id} attendee={a} meeting={meeting} editable={editable} />
            ))}
          </ul>
          {editable && !anyVote && (
            <button type="button" className="link-button small" disabled={prepare.isPending} onClick={() => prepare.mutate()}>
              {g.refreshAttendance}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function AttendanceRow({ attendee: a, meeting, editable }: { attendee: Attendee; meeting: Meeting; editable: boolean }) {
  const { t, locale } = useI18n();
  const g = t.governance;
  const [holder, setHolder] = useState(a.proxyHolder);
  const update = useGovernanceMutation<UpdateAttendance, void>((api, base, input) => api(`${base}/governance/attendance/${a.id}`, { method: 'PATCH', body: input }));
  const chair = useGovernanceMutation<string | null, void>((api, base, attendanceId) => api(`${base}/governance/meetings/${meeting.id}/chair`, { method: 'POST', body: { attendanceId } }));
  const votes = meeting.voteBasis === 'quote_parts' ? pct(a.votes, meeting.totalVotes ?? 0, locale) : String(a.votes);
  return (
    <li className={`attendee attendee-${a.status}`}>
      <div className="attendee-who">
        <strong>{a.name}</strong>
        <span className="muted small">
          {[a.units, meeting.kind === 'board' ? '' : `${votes}`].filter(Boolean).join(' · ')}
          {a.votingSuspended && (
            <span className="badge badge-urgent" title={a.suspensionReason}>
              {g.noVote}
            </span>
          )}
          {meeting.kind === 'board' && a.isChair && <span className="badge">{g.chair}</span>}
          {meeting.kind === 'board' && !a.isChair && editable && (
            <button type="button" className="link-button small" disabled={chair.isPending} onClick={() => chair.mutate(a.id)}>
              {g.makeChair}
            </button>
          )}
        </span>
      </div>
      <div className="segmented" role="group" aria-label={a.name}>
        {(['present', 'proxy', 'absent'] as const).map((status) => (
          <button key={status} type="button" className={a.status === status ? 'active' : undefined} disabled={!editable || update.isPending} onClick={() => update.mutate({ status, proxyHolder: holder })}>
            {g[status]}
          </button>
        ))}
      </div>
      {a.status === 'proxy' && (
        <input
          className="proxy-holder"
          placeholder={g.proxyHolder}
          value={holder}
          disabled={!editable}
          onChange={(e) => setHolder(e.target.value)}
          onBlur={() => holder !== a.proxyHolder && update.mutate({ status: 'proxy', proxyHolder: holder })}
        />
      )}
    </li>
  );
}

/** Vote on a resolution: unanimity in one click, or each attendee's choice, with the result live. */
export function VotePanel({ meeting, item, editable }: { meeting: Meeting; item: AgendaItem; editable: boolean }) {
  const { t, locale } = useI18n();
  const g = t.governance;
  const [detailed, setDetailed] = useState(false);
  const record = useGovernanceMutation<RecordVotes, void>((api, base, input) => api(`${base}/governance/items/${item.id}/votes`, { method: 'POST', body: input }));
  const unanimous = useGovernanceMutation<void, void>((api, base) => api(`${base}/governance/items/${item.id}/votes/unanimous`, { method: 'POST' }));
  const close = useGovernanceMutation<void, AgendaItem>((api, base) => api(`${base}/governance/items/${item.id}/votes/close`, { method: 'POST' }));

  if (!meeting.quorum || !meeting.voteBasis || !meeting.totalVotes || !meeting.totalVoters) return <p className="muted small">{g.needAttendance}</p>;
  const eligible = meeting.attendance.filter((a) => a.status !== 'absent' && !a.votingSuspended);
  const chair = meeting.attendance.find((a) => a.isChair);
  const castingVote = meeting.castingVote && chair ? (item.votes[chair.id] ?? null) : null;
  const live = tallyVote(meeting.attendance, item.votes, { majority: item.majority ?? 'simple', basis: meeting.voteBasis, totalVotes: meeting.totalVotes, totalVoters: meeting.totalVoters, castingVote });
  const show = (n: number) => (meeting.voteBasis === 'quote_parts' ? pct(n, meeting.totalVotes!, locale) : String(n));
  const counted = Object.keys(item.votes).length > 0;
  const busy = record.isPending || unanimous.isPending || close.isPending;

  return (
    <div className="vote">
      <div className="vote-head">
        <strong>{g.vote}</strong>
        <span className="badge">{g.majorities[item.majority ?? 'simple']}</span>
        {counted && <span className={`badge ${live.adopted ? 'badge-status-in_progress' : 'badge-urgent'}`}>{item.voteResult ? g.voteClosed : live.adopted ? g.wouldPass : g.wouldFail}{live.castingVote && !item.voteResult ? ` · ${g.castingUsed}` : ''}</span>}
      </div>
      {counted && (
        <p className="small">
          {g.tally(show(live.for), show(live.against), show(live.abstain))} <span className="muted">({g.eligibleLine(show(live.eligibleVotes))})</span>
        </p>
      )}
      {editable && (
        <div className="form-actions">
          <button type="button" className="button button-small" disabled={busy || eligible.length === 0} onClick={() => unanimous.mutate()}>
            {g.unanimous}
          </button>
          <button type="button" className="button button-small" onClick={() => setDetailed(!detailed)}>
            {g.detailedVote} {detailed ? '▴' : '▾'}
          </button>
          {counted && (
            <button type="button" className="button button-small button-primary" disabled={busy} onClick={() => close.mutate()}>
              {g.closeVote}
            </button>
          )}
        </div>
      )}
      {detailed && (
        <ul className="ballots">
          {eligible.map((a) => (
            <li key={a.id}>
              <span>
                {a.name} <span className="muted small">{meeting.kind === 'board' ? '' : show(a.votes)}</span>
              </span>
              <span className="segmented">
                {(['for', 'against', 'abstain'] as const).map((choice) => (
                  <button
                    key={choice}
                    type="button"
                    className={item.votes[a.id] === choice ? `active choice-${choice}` : undefined}
                    disabled={!editable || busy}
                    onClick={() => record.mutate({ choices: [{ attendanceId: a.id, choice: item.votes[a.id] === choice ? null : choice }] })}
                  >
                    {g[choice]}
                  </button>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
      {(record.isError || unanimous.isError || close.isError) && <Alert>{errorMessage(record.error ?? unanimous.error ?? close.error, t)}</Alert>}
      {detailed && <p className="muted small">{g.rulesToValidate}</p>}
    </div>
  );
}
